import { useId, type Ref, type TextareaHTMLAttributes } from 'react';

import { cn } from '@/shared/lib/cn';

import { FieldLabel } from './FieldLabel';

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'id'
> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /**
   * Texto do icone de ajuda (i). Sem ele, o campo usa o dicionario de ajuda pela
   * tela e pelo rotulo; false desliga a ajuda.
   */
  help?: string | false | undefined;

  // A referencia existe para quem precisa da posicao do cursor. Inserir uma variavel
  // no ponto onde a pessoa esta escrevendo exige alcancar o elemento; sem isso, a
  // insercao so consegue anexar ao fim do texto.
  ref?: Ref<HTMLTextAreaElement>;
}

export function Textarea({
  label,
  error,
  hint,
  help,
  className,
  ref,
  ...rest
}: TextareaProps) {
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel
        htmlFor={fieldId}
        label={label}
        className="text-xs font-semibold text-ink-700"
        help={help}
      />
      <textarea
        ref={ref}
        id={fieldId}
        rows={3}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          'w-full rounded-control border bg-surface px-3 py-2 text-sm text-ink-900 shadow-(--shadow-xs)',
          'placeholder:text-ink-400 transition-[border-color,box-shadow]',
          'duration-(--duration-fast) focus:outline-none focus:ring-4',
          error
            ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/15'
            : 'border-line-strong hover:border-ink-400 focus:border-brand-500 focus:ring-brand-500/15',
          className,
        )}
        {...rest}
      />
      {hint && !error ? (
        <p id={hintId} className="text-2xs text-ink-500">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-2xs font-medium text-danger-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
